import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, timer } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { AuthStateService } from '../services/auth-state.service';

/**
 * Custom preloading strategy that intelligently preloads routes based on user state:
 * - Preloads sign-in route when user is NOT logged in (default landing page)
 * - Preloads home route when user IS logged in
 * 
 * This significantly improves initial page load performance by starting chunk downloads
 * before navigation begins.
 * 
 * Note: This works with both loadChildren and loadComponent routes.
 * For standalone components (loadComponent), the load function will be called
 * to preload the component chunk.
 */
@Injectable({
  providedIn: 'root'
})
export class HomePreloadStrategy implements PreloadingStrategy {
  constructor(private authStateService: AuthStateService) {}

  preload(route: Route, load: () => Observable<any>): Observable<any> {
    const isLoggedIn = this.authStateService.isLoggedIn;
    
    // Preload sign-in route immediately when user is NOT logged in
    // The sign-in route uses dynamic path ':tenantShortName', so we check if it's a dynamic route
    // and user is not logged in
    if (!isLoggedIn) {
      // Check if this is the sign-in route (dynamic tenant route :tenantShortName)
      if (route.path && route.path.startsWith(':')) {
        // This is likely the sign-in route (dynamic tenant route)
        // Preload immediately for faster initial page load
        return load();
      }
    }
    
    // If this is the home route and user is logged in, preload immediately
    if (route.path === 'home' && isLoggedIn) {
      // Preload immediately for home route when logged in
      return load();
    }

    // For other routes, preload after a short delay to allow priority routes to load first
    return timer(200).pipe(mergeMap(() => load()));
  }
}
